import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { creditos, fecha, hora } from "../lib/formato";
import { Aviso, Cabecera, Cargando, Etiqueta, FotoSocio, Panel, TarjetaDato, Vacio } from "../componentes/piezas";
import BuscadorSocios, { type SocioResumen } from "../componentes/BuscadorSocios";
import { useIdioma } from "../lib/idioma";

interface Acceso {
  id: string;
  fecha: string;
  tipo: string;
  socio: { id: string; numeroSocio: number; nombre: string; apellidos: string; fotoUrl: string | null };
}

interface Presente {
  id: string;
  numeroSocio: number;
  nombre: string;
  apellidos: string;
  fotoUrl: string | null;
  desde: string;
}

export default function Accesos() {
  const { t } = useIdioma();
  const clienteConsultas = useQueryClient();
  const [error, setError] = useState("");
  const [ultimo, setUltimo] = useState<{
    nombre: string;
    numeroSocio: number;
    saldoCentimos: number;
    fechaCaducidad: string | null;
    fotoUrl: string | null;
    apellidos: string;
  } | null>(null);

  const accesos = useQuery({ queryKey: ["accesos"], queryFn: () => api<Acceso[]>("/accesos") });
  const presentes = useQuery({ queryKey: ["presentes"], queryFn: () => api<Presente[]>("/accesos/presentes") });

  async function registrar(socio: SocioResumen, tipo: "ENTRADA" | "SALIDA") {
    setError("");
    try {
      const respuesta = await api<{ socio: typeof ultimo }>("/accesos", {
        metodo: "POST",
        cuerpo: { socioId: socio.id, tipo },
      });
      setUltimo(respuesta.socio);
      void clienteConsultas.invalidateQueries({ queryKey: ["accesos"] });
      void clienteConsultas.invalidateQueries({ queryKey: ["presentes"] });
    } catch (fallo) {
      setUltimo(null);
      setError(fallo instanceof Error ? fallo.message : t("ac.fallo"));
    }
  }

  return (
    <>
      <Cabecera titulo={t("ac.titulo")} descripcion={t("ac.descripcion")} />

      <Aviso tipo="error">{error}</Aviso>

      <div className="rejilla dos" style={{ alignItems: "start" }}>
        <div className="columna" style={{ gap: 16 }}>
          <Panel titulo={t("ac.registrarEntrada")}>
            <BuscadorSocios
              autoFoco
              alElegir={(socio) => void registrar(socio, "ENTRADA")}
              marcador={t("ac.identificar")}
            />
            <p className="suave pequeno" style={{ marginBottom: 0, marginTop: 10 }}>
              {t("ac.alElegir")}
            </p>
          </Panel>

          {ultimo && (
            <Panel>
              <div className="fila" style={{ gap: 14 }}>
                <FotoSocio socio={ultimo} grande />
                <div>
                  <h2>
                    {ultimo.nombre} {ultimo.apellidos}
                  </h2>
                  <div className="suave pequeno">{t("ac.socioNumero", { numero: ultimo.numeroSocio })}</div>
                  <div className="fila" style={{ marginTop: 8 }}>
                    <Etiqueta tipo="verde">{t("ac.accesoRegistrado")}</Etiqueta>
                    <span className="pequeno suave">
                      {t("ac.cartera", { importe: creditos(ultimo.saldoCentimos) })}
                    </span>
                  </div>
                  <div className="pequeno suave" style={{ marginTop: 4 }}>
                    {t("ac.cuotaValida", { fecha: fecha(ultimo.fechaCaducidad) })}
                  </div>
                </div>
              </div>
              <Link className="boton bloque" to={`/mostrador/${accesos.data?.[0]?.socio.id ?? ""}`} style={{ marginTop: 14 }}>
                {t("ac.irAlMostrador")}
              </Link>
            </Panel>
          )}

          <div className="rejilla dos">
            <TarjetaDato etiqueta={t("ac.dentroAhora")} valor={presentes.data?.length ?? 0} />
            <TarjetaDato
              etiqueta={t("ac.accesosHoy")}
              valor={accesos.data?.filter((a) => a.tipo === "ENTRADA").length ?? 0}
            />
          </div>
        </div>

        <div className="columna" style={{ gap: 16 }}>
          <Panel titulo={t("ac.enElLocal")} sinCuerpo>
            {presentes.isLoading ? (
              <Cargando />
            ) : !presentes.data || presentes.data.length === 0 ? (
              <Vacio>{t("ac.nadieDentro")}</Vacio>
            ) : (
              <div className="panel-cuerpo">
                <div className="columna">
                  {presentes.data.map((p) => (
                    <div key={p.id} className="fila separada">
                      <div className="fila">
                        <FotoSocio socio={p} />
                        <div>
                          <Link to={`/socios/${p.id}`}>
                            <strong>
                              {p.nombre} {p.apellidos}
                            </strong>
                          </Link>
                          <div className="suave pequeno">{t("ac.socioNumero", { numero: p.numeroSocio })}</div>
                        </div>
                      </div>
                      <span className="suave pequeno">{t("ac.desde", { hora: hora(p.desde) })}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Panel>

          <Panel titulo={t("ac.deHoy")} sinCuerpo>
            {accesos.isLoading ? (
              <Cargando />
            ) : !accesos.data || accesos.data.length === 0 ? (
              <Vacio>{t("ac.ningunoHoy")}</Vacio>
            ) : (
              <div className="tabla-envoltorio">
                <table className="tabla">
                  <thead>
                    <tr>
                      <th>{t("ac.hora")}</th>
                      <th>{t("ac.socio")}</th>
                      <th>{t("ac.tipo")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accesos.data.map((a) => (
                      <tr key={a.id}>
                        <td className="pequeno tabular suave">{hora(a.fecha)}</td>
                        <td>
                          <Link to={`/socios/${a.socio.id}`}>
                            #{a.socio.numeroSocio} {a.socio.nombre} {a.socio.apellidos}
                          </Link>
                        </td>
                        <td>
                          <Etiqueta tipo={a.tipo === "ENTRADA" ? "verde" : ""}>
                            {a.tipo === "ENTRADA" ? t("ac.entrada") : t("ac.salida")}
                          </Etiqueta>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </div>
      </div>
    </>
  );
}
